'use client'

import { useQuery } from '@tanstack/react-query'
import clsx from 'clsx'
import { useTranslations } from 'next-intl'
import { useEffect, useState } from 'react'

import ChevronDownIcon from '@/assets/icons/chevron-down.svg'
import CrossIcon from '@/assets/icons/tasks-feed/cross.svg'
import SortIcon from '@/assets/icons/tasks-feed/sort.svg'
import type { TasksView } from '@/lib/tasks/tasksTabs'

import {
  FeedCheckList,
  type FeedMultiFilter,
  FeedSortList
} from './FeedMenuLists'
import { FeedPopover } from './FeedPopover'
import { FeedSheet } from './FeedSheet'
import {
  TASKS_FEED_SORT_LABEL_KEY,
  type TasksFeedChip,
  type TasksFeedSortId
} from './tasksFeedNav.constants'
import S from './TasksFeedNav.module.scss'

export type { FeedMultiFilter } from './FeedMenuLists'

/** Picked values of the sheet's filters, by filter id. */
export type FeedPendingFilters = Record<string, string[]>

const FILTER_COUNT_DEBOUNCE_MS = 250

export type TasksFeedNavProps<C extends string> = {
  view: TasksView
  onViewChange: (view: TasksView) => void
  chips: TasksFeedChip<C>[]
  selectedChip: C
  onChipChange: (chip: C) => void
  chipsAriaLabel: string
  /** Filters with no options are left out. */
  filters: FeedMultiFilter[]
  sort: TasksFeedSortId
  sortOptions: readonly TasksFeedSortId[]
  onSortChange: (sort: TasksFeedSortId) => void
  /** Shown while any filter is on. */
  onClearAll?: () => void
  /** How many cards the phone sheet's pending filters would show. */
  countPending?: (pending: FeedPendingFilters) => {
    queryKey: readonly unknown[]
    queryFn: () => Promise<number>
  }
}

const VIEWS: readonly TasksView[] = ['explore', 'my']

export default function TasksFeedNav<C extends string>({
  view,
  onViewChange,
  chips,
  selectedChip,
  onChipChange,
  chipsAriaLabel,
  filters: allFilters,
  sort,
  sortOptions,
  onSortChange,
  onClearAll,
  countPending
}: TasksFeedNavProps<C>) {
  const t = useTranslations('tasksPage.feedNav')
  const [sheet, setSheet] = useState<'filters' | 'sort' | null>(null)
  const filters = allFilters.filter(filter =>
    filter.groups.some(group => group.options.length > 0)
  )
  const sortItems = sortOptions.map(value => ({
    value,
    label: t(TASKS_FEED_SORT_LABEL_KEY[value])
  }))
  const sortLabel = t(TASKS_FEED_SORT_LABEL_KEY[sort])
  const pickedCount = filters.reduce((n, f) => n + f.value.length, 0)
  const sheetTitle = filters.length === 1 ? filters[0].label : t('filters')

  return (
    <nav className={S.root} aria-label={t('viewAriaLabel')}>
      <div className={S.views} role="group" aria-label={t('viewAriaLabel')}>
        {VIEWS.map(id => (
          <button
            key={id}
            type="button"
            className={clsx(S.view, id === view && S.viewActive)}
            aria-pressed={id === view}
            onClick={() => {
              onViewChange(id)
            }}
          >
            {id === 'explore' ? t('explore') : t('myTasks')}
          </button>
        ))}
      </div>

      <span className={S.divider} aria-hidden />

      <div className={S.chips} role="group" aria-label={chipsAriaLabel}>
        {chips.map(chip => {
          const isActive = !chip.soon && chip.id === selectedChip
          return (
            <span key={chip.id} className={S.chipWrap}>
              <button
                type="button"
                className={clsx(
                  S.chip,
                  isActive && S.chipActive,
                  chip.soon && S.chipSoon
                )}
                disabled={chip.soon}
                aria-pressed={chip.soon ? undefined : isActive}
                onClick={() => {
                  onChipChange(chip.id)
                }}
              >
                {chip.icon ? (
                  <span className={S.chipIcon} aria-hidden>
                    {chip.icon}
                  </span>
                ) : null}
                {chip.label}
                {chip.count ? (
                  <span className={S.count}>{chip.count}</span>
                ) : null}
              </button>
              {chip.soon ? (
                <span className={S.soonBadge}>{t('soonBadge')}</span>
              ) : null}
            </span>
          )
        })}
      </div>

      <div className={S.actions}>
        {onClearAll ? (
          <button type="button" className={S.chip} onClick={onClearAll}>
            <CrossIcon className={S.chipIcon} aria-hidden />
            {t('clearAll')}
          </button>
        ) : null}
        {filters.map(filter => (
          <FeedPopover
            key={filter.id}
            trigger={({ open, toggle, menuId }) => (
              <button
                type="button"
                className={clsx(S.chip, S.trigger, open && S.chipActive)}
                aria-expanded={open}
                aria-controls={menuId}
                aria-label={filter.ariaLabel}
                onClick={toggle}
              >
                {filter.label}
                {filter.value.length > 0 ? (
                  <span className={S.count}>{filter.value.length}</span>
                ) : null}
                <ChevronDownIcon
                  className={clsx(S.chevron, open && S.chevronOpen)}
                  aria-hidden
                />
              </button>
            )}
          >
            {() => (
              <FeedCheckList
                filter={filter}
                value={filter.value}
                onChange={filter.onChange}
              />
            )}
          </FeedPopover>
        ))}
        <span className={S.divider} aria-hidden />
        <FeedPopover
          trigger={({ open, toggle, menuId }) => (
            <button
              type="button"
              className={clsx(S.chip, S.trigger, open && S.chipActive)}
              aria-expanded={open}
              aria-controls={menuId}
              aria-label={t('sortAriaLabel')}
              onClick={toggle}
            >
              {sortLabel}
              <SortIcon className={S.chipIcon} aria-hidden />
            </button>
          )}
        >
          {close => (
            <FeedSortList
              options={sortItems}
              value={sort}
              ariaLabel={t('sortAriaLabel')}
              onChange={next => {
                onSortChange(next as TasksFeedSortId)
                close()
              }}
            />
          )}
        </FeedPopover>
      </div>

      <div className={S.mobileActions}>
        {filters.length > 0 ? (
          <button
            type="button"
            className={clsx(S.chip, S.trigger)}
            aria-haspopup="dialog"
            onClick={() => {
              setSheet('filters')
            }}
          >
            {sheetTitle}
            {pickedCount > 0 ? (
              <span className={S.count}>{pickedCount}</span>
            ) : null}
            <ChevronDownIcon className={S.chevron} aria-hidden />
          </button>
        ) : null}
        <button
          type="button"
          className={clsx(S.chip, S.iconButton)}
          aria-haspopup="dialog"
          aria-label={t('sortAriaLabel')}
          onClick={() => {
            setSheet('sort')
          }}
        >
          <SortIcon className={S.chipIcon} aria-hidden />
        </button>
      </div>

      {sheet === 'filters' ? (
        <FiltersSheet
          title={sheetTitle}
          filters={filters}
          countPending={countPending}
          onClose={() => {
            setSheet(null)
          }}
        />
      ) : null}
      <FeedSheet
        open={sheet === 'sort'}
        onOpenChange={open => {
          setSheet(open ? 'sort' : null)
        }}
        title={t('sortTitle')}
        description={t('sortAriaLabel')}
      >
        <FeedSortList
          options={sortItems}
          value={sort}
          ariaLabel={t('sortAriaLabel')}
          size="sheet"
          onChange={next => {
            onSortChange(next as TasksFeedSortId)
            setSheet(null)
          }}
        />
      </FeedSheet>
    </nav>
  )
}

type FiltersSheetProps = {
  title: string
  filters: FeedMultiFilter[]
  countPending: TasksFeedNavProps<string>['countPending']
  onClose: () => void
}

/** Picks stay pending until «Show N tasks»; mounted only while open. */
function FiltersSheet({
  title,
  filters,
  countPending,
  onClose
}: FiltersSheetProps) {
  const t = useTranslations('tasksPage.feedNav')
  const [pending, setPending] = useState<FeedPendingFilters>(() =>
    Object.fromEntries(filters.map(f => [f.id, [...f.value]]))
  )
  const [countedPending, setCountedPending] = useState(pending)
  useEffect(() => {
    if (pending === countedPending) {
      return
    }
    const timer = window.setTimeout(
      () => setCountedPending(pending),
      FILTER_COUNT_DEBOUNCE_MS
    )
    return () => window.clearTimeout(timer)
  }, [countedPending, pending])
  const countQuery = countPending?.(countedPending)
  const { data: count } = useQuery({
    queryKey: countQuery?.queryKey ?? ['tasks', 'feed-count', 'none'],
    queryFn: countQuery?.queryFn ?? (() => Promise.resolve(0)),
    enabled: Boolean(countQuery)
  })
  const hasPicks = Object.values(pending).some(values => values.length > 0)

  return (
    <FeedSheet
      open
      onOpenChange={open => {
        if (!open) {
          onClose()
        }
      }}
      title={title}
      description={t('filtersAriaLabel')}
      footer={
        <>
          <button
            type="button"
            className={S.sheetClear}
            disabled={!hasPicks}
            onClick={() => {
              setPending(Object.fromEntries(filters.map(f => [f.id, []])))
            }}
          >
            {t('clearAll')}
          </button>
          <button
            type="button"
            className={S.sheetApply}
            onClick={() => {
              for (const filter of filters) {
                filter.onChange(pending[filter.id] ?? [])
              }
              onClose()
            }}
          >
            {pending !== countedPending || count === undefined
              ? t('showTasksPending')
              : t('showTasks', { count })}
          </button>
        </>
      }
    >
      {filters.map(filter => (
        <FeedCheckList
          key={filter.id}
          filter={filter}
          value={pending[filter.id] ?? []}
          size="sheet"
          onChange={next => {
            setPending(prev => ({ ...prev, [filter.id]: next }))
          }}
        />
      ))}
    </FeedSheet>
  )
}
